import { useEffect, useRef } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  ActiveNavMenu,
  initialState,
  isMiddlePaneAction,
  useNavigationState,
} from "@/zustand/NavigationState";
import { getChatFromLocalDB } from "@/indexedDB/services/chats";
import { ChatType } from "@/server/types/chat";

export const useChatRouteSync = () => {
  const navigate = useNavigate();
  const { chatId } = useParams<{ chatId?: string }>();

  const resetNavigationState = useNavigationState(
    (s) => s.resetNavigationState,
  );

  const setActiveChatId = useNavigationState((s) => s.setActiveChatId);
  const setActiveNavMenu = useNavigationState((s) => s.setActiveNavMenu);

  /**
   * Tracks the last synced chatId to guard against redundant effect runs.
   *
   * Sentinel "__UNINITIALIZED__" ensures the effect always runs on first mount —
   * even when chatId is undefined — so persisted activeChatId from localStorage
   * is always cleared on a fresh load at the root route.
   */
  const lastSyncedChatId = useRef<string>("__UNINITIALIZED__");

  useEffect(() => {
    const routeKey = chatId ?? "";

    if (routeKey === lastSyncedChatId.current) {
      return;
    }
    const prevSyncedChatId = lastSyncedChatId.current;
    lastSyncedChatId.current = routeKey;

    if (!chatId) {
      // When a middle-column view (payments pane or settings category) is
      // open, its useStart*View() hook navigates to / (no chatId) to clear the
      // URL mismatch. Don't reset state here — resetting would wipe the view
      // the instant it opens (docs/settings-middle-views/design.md, invariant 1).
      const currentAction = useNavigationState.getState().activeChatUserAction;
      if (isMiddlePaneAction(currentAction)) {
        return;
      }
      const activeMenuType = useNavigationState.getState().activeNavMenu;
      if (prevSyncedChatId === "__UNINITIALIZED__") {
        // First load: full reset including the CREATE_AI_CHAT onboarding default.
        resetNavigationState();
      } else {
        // Back navigation from a chat: reset everything but don't re-trigger the
        // onboarding form — user should land on the chat list, not the create form.
        useNavigationState.setState({
          ...initialState,
          activeChatUserAction: null,
        });
      }
      setActiveNavMenu(activeMenuType);
      return;
    }

    const activeChatId = useNavigationState.getState().activeChatId;
    if (chatId === activeChatId) {
      return;
    }

    const syncChat = async () => {
      const chatExists = await getChatFromLocalDB(chatId);

      if (!chatExists) {
        navigate("/", { replace: true });
        return;
      }
      await setActiveChatId(chatId);
      setActiveNavMenu(
        chatExists.type === ChatType.GROUP
          ? chatExists.hubId
            ? ActiveNavMenu.AI_CHAT
            : ActiveNavMenu.GROUP_CHATS
          : ActiveNavMenu.P2P_CHATS,
      );
    };

    // eslint-disable-next-line no-console
    syncChat().catch(console.error);
  }, [
    chatId,
    navigate,
    resetNavigationState,
    setActiveNavMenu,
    setActiveChatId,
  ]);
};
